/**
 * The Dayvise web application root (prompt.md §37, §39, §53).
 *
 * This SPA is the reference implementation of the Dayvise client protocol: it
 * exercises every API surface and the realtime run stream, and the Tauri desktop
 * shell mounts the same shared packages rather than forking this tree.
 *
 * Routing is code-based (not file-based) so the route table is a typed value a
 * test can import, and so nothing about the product's navigation depends on a
 * build-time code generation step.
 */

import { RouterProvider, createRootRoute, createRoute, createRouter, Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect } from "react";
import { DayviseBrand, DayviseTheme, MUTED_TEXT, PlaneBadge, ThemeToggle, rowStyle, stackStyle } from "@dayvise/react-ui";
import { DayviseProvider, InputRequestPopupHost, PresenceReporter, SessionProvider, useSession, useThemeScheme } from "@dayvise/react-app-core";

import { resolveApiBaseUrl } from "./config.ts";
import { LoginPage, browserTokenStore } from "./routes/Login.tsx";
import { PaymentPage } from "./routes/Payment.tsx";
import { ConversationsPage } from "./routes/Conversations.tsx";
import { ConversationDetailPage } from "./routes/ConversationDetail.tsx";
import {
  AdminPage,
  AgentsPage,
  FilesPage,
  MemoryPage,
  NotificationsPage,
  BillingPage,
  BoardsPage,
  ProvidersPage,
  RunsPage,
  SchedulesPage,
  SnapshotsPage,
  StoragePage,
  ToolsPage,
  UsagePage,
  LearningPage,
  ProactivePage,
  RuntimePage,
  YoloPage,
} from "./routes/Pages.tsx";

export const NAVIGATION = [
  { to: "/", label: "Conversations" },
  { to: "/agents", label: "Agents" },
  { to: "/runs", label: "Runs" },
  { to: "/schedules", label: "Schedules" },
  { to: "/memory", label: "Memory" },
  { to: "/files", label: "Files" },
  { to: "/usage", label: "Usage" },
  { to: "/boards", label: "Boards" },
  { to: "/learning", label: "Learning" },
  { to: "/billing", label: "Billing" },
  { to: "/notifications", label: "Notifications" },
  { to: "/settings/providers", label: "Providers" },
  { to: "/settings/storage", label: "Storage" },
  { to: "/settings/tools", label: "Tools" },
  { to: "/settings/snapshots", label: "Snapshots" },
  { to: "/admin", label: "Admin" },
] as const;

/** Exported so a test can render the signed-in shell without a router history. */
export function AppLayout() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { session } = useSession();
  const navigate = useNavigate();

  if (pathname === "/pay") return <Outlet />;

  if (session.status !== "signed_in") {
    return <LoginPage />;
  }

  return (
    <div style={{ ...stackStyle("var(--dayvise-space-md)"), padding: "var(--dayvise-space-md)", minHeight: "100vh", boxSizing: "border-box" }}>
      <header style={stackStyle("var(--dayvise-space-sm)")}>
        <div style={rowStyle("var(--dayvise-space-sm)", "space-between")}>
          <span style={{ ...rowStyle("var(--dayvise-space-sm)"), fontSize: "var(--dayvise-font-size-lg)", fontWeight: "var(--dayvise-font-weight-semibold)" }}>
            <DayviseBrand />
            {session.workspace ? <PlaneBadge kind={session.workspace.kind} /> : null}
          </span>
        </div>
        <nav aria-label="Primary" style={rowStyle("var(--dayvise-space-md)", "flex-start")}>
          {NAVIGATION.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              style={{
                color: pathname === item.to ? "var(--dayvise-color-accent)" : "var(--dayvise-color-text-primary)",
                textDecoration: "none",
                fontSize: "var(--dayvise-font-size-sm)",
                borderBottom: pathname === item.to ? "2px solid var(--dayvise-color-accent)" : "2px solid transparent",
                paddingBottom: "var(--dayvise-space-xxs)",
              }}
            >
              {item.label}
            </Link>
          ))}
        </nav>
        {session.workspace ? (
          <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-xs)" }}>
            {session.account?.label} · {session.workspace.name}
          </span>
        ) : null}
      </header>
      <hr style={{ border: "none", borderTop: "1px solid var(--dayvise-color-border)", margin: 0 }} />
      <PresenceReporter platform="web" />
      {/* A question that arrives while another screen is open is announced here, not
          stored: the popup reads the same durable pending list the inbox renders. */}
      <InputRequestPopupHost
        desktopNotifications
        onOpenConversation={(conversationId) => {
          void navigate({ to: "/c/$conversationId", params: { conversationId } });
        }}
      />
      <main style={{ flex: 1, minHeight: 0 }}>
        <Outlet />
      </main>
    </div>
  );
}

const rootRoute = createRootRoute({ component: AppLayout });

/**
 * The OAuth/local redirect target (`endpoints().app + /auth/callback`).
 *
 * The redeem itself runs on the login page while signed out — this route must
 * NOT redirect in beforeLoad, or the ?code= would be stripped before the
 * redeem reads it. The signed-out layout renders the login page instead of the
 * Outlet, so this component only mounts AFTER sign-in completed; its one job
 * is to put the user where they expected to land.
 */
function AuthCallbackRedirect() {
  useEffect(() => {
    void router.navigate({ to: "/", replace: true });
  }, []);
  return null;
}

export const routeTree = rootRoute.addChildren([
  createRoute({ getParentRoute: () => rootRoute, path: "/", component: ConversationsPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/auth/callback", component: AuthCallbackRedirect }),
  createRoute({ getParentRoute: () => rootRoute, path: "/c/$conversationId", component: ConversationDetailPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/agents", component: AgentsPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/runs", component: RunsPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/schedules", component: SchedulesPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/memory", component: MemoryPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/files", component: FilesPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/usage", component: UsagePage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/boards", component: BoardsPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/learning", component: LearningPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/billing", component: BillingPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/pay", component: PaymentPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/notifications", component: NotificationsPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/settings/providers", component: ProvidersPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/settings/storage", component: StoragePage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/settings/tools", component: ToolsPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/settings/snapshots", component: SnapshotsPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/settings/proactive", component: ProactivePage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/settings/yolo", component: YoloPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/settings/runtime", component: RuntimePage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/admin", component: AdminPage }),
]);

export const router = createRouter({ routeTree, defaultPreload: "intent" });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export function App() {
  // Jetblack dark by default; an explicit choice persists across reloads.
  const [scheme, setScheme] = useThemeScheme();
  return (
    <DayviseTheme scheme={scheme}>
      <DayviseProvider config={{ baseUrl: resolveApiBaseUrl() }} tokenStore={browserTokenStore}>
        <SessionProvider>
          {/* Outside the router so changing the theme never re-runs a route loader. */}
          <div style={{ position: "fixed", bottom: "var(--dayvise-space-sm)", right: "var(--dayvise-space-sm)", zIndex: 40 }}>
            <ThemeToggle scheme={scheme} onChange={setScheme} />
          </div>
          <RouterProvider router={router} />
        </SessionProvider>
      </DayviseProvider>
    </DayviseTheme>
  );
}
